import { Box, Typography } from '@mui/material';
import { Global, css } from '@emotion/react';
import Divider from 'components/divider';
import LoadingSpinner from 'components/loading-spinner';
import Tooltip from 'components/tooltip';
import { useGet } from 'hooks/use-get';
import { ArrowUpDown, CircleHelp, ClipboardCheck, FishSymbol, Hand, Ship, Computer } from 'lucide-react';
import Mui from 'materials';
import React, { useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { useParams } from 'react-router-dom';
import _ from 'underscore';
import titleCase from 'utils/title-case';
import { getInventoryLog } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/get-ofd-settings';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { EventName } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings.type';
import { drawerOverflowStyle, GridWrapper } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.feedInventoryTab.dialog.log';

const getUtcToday = () => new Date().toISOString().slice(0, 10);
const getUtcDaysAgo = (days: number) => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - days)).toISOString().slice(0, 10);
};

const iconMap: Record<string, React.ElementType> = {
  'Manual update': Hand,
  Consumption: FishSymbol,
  'Exp. Consumption': FishSymbol,
  Delivery: Ship,
  Forecast: FishSymbol,
  'System estimate': Computer,
};

type EmptyEventRowProps = {
  date: string;
  startingInventory: number;
  showStartingInventory: boolean;
};

const EmptyEventRow: React.FC<EmptyEventRowProps> = ({ date, startingInventory, showStartingInventory }) => {
  const today = getUtcToday();
  return (
    <GridWrapper py={2} sx={{ backgroundColor: date === today ? '#E6F2FA' : '#F7F8F8', borderRadius: '8px' }}>
      <Box px={1} fontSize={14}>
        <FormattedDate value={date} weekday="short" day="2-digit" month="short" />
      </Box>
      <Box />
      <Box display={'flex'} alignItems={'center'} gap={2} px={2} py={4} minWidth={0}>
        {date === today ? (
          <>
            <ClipboardCheck size={20} />
            <Typography fontSize={14}>
              <FormattedMessage id={`${PAGE_NAME_ID}.today`} />
            </Typography>
          </>
        ) : (
          <Typography fontSize={14}>
            <FormattedMessage id={`${PAGE_NAME_ID}.emptyArray`} />
          </Typography>
        )}
      </Box>
      <Box />
      {showStartingInventory ? (
        <Box fontSize={14} fontWeight={700} display={'flex'} width={'100%'} justifyContent={'flex-end'} pr={2} whiteSpace={'nowrap'}>
          <FormattedNumber
            value={startingInventory}
            unit={'kilogram'}
            style="unit"
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </Box>
      ) : (
        <Box />
      )}
    </GridWrapper>
  );
};

const Headers: React.FC = () => (
  <GridWrapper>
    <Typography width={'100%'} px={1} fontWeight={'bold'} fontSize={12}>
      <FormattedMessage id={`${PAGE_NAME_ID}.day`} />
    </Typography>
    <Typography width={'100%'} textAlign={'center'} fontWeight={'bold'} fontSize={12}>
      <FormattedMessage id={`${PAGE_NAME_ID}.dataInput`} />
    </Typography>
    <Typography width={'100%'} px={2} fontWeight={'bold'} fontSize={12}>
      <FormattedMessage id={`${PAGE_NAME_ID}.event`} />
    </Typography>
    <Typography width={'100%'} display={'flex'} justifyContent={'flex-end'} pr={2} fontWeight={'bold'} fontSize={12}>
      <FormattedMessage id={`${PAGE_NAME_ID}.adjustment`} />
    </Typography>
    <Typography width={'100%'} display={'flex'} justifyContent={'flex-end'} pr={2} fontWeight={'bold'} fontSize={12}>
      <FormattedMessage id={`${PAGE_NAME_ID}.startInventory`} />
    </Typography>
  </GridWrapper>
);

const FeedInventoryLog: React.FC<{}> = () => {
  const [isAscending, setIsAscending] = useState(false);
  const params = useParams<OFD2Params>();

  const { data: inventoryLogData, isFetching } = useGet(
    ['inventoryLog', params.globalSiteId],
    () => getInventoryLog(params.globalSiteId),
    {
      enabled: !!params.globalSiteId,
    }
  );

  const latestManualUpdateDate = useMemo(
    () =>
      inventoryLogData?.reduce<string | null>((latestDate, logItem) => {
        if (!logItem.events.some((eventItem) => eventItem.event === EventName.MANUAL_UPDATE)) return latestDate;
        return latestDate === null || logItem.utcDate > latestDate ? logItem.utcDate : latestDate;
      }, null) ?? null,
    [inventoryLogData]
  );
  const recentHistoryStartDate = getUtcDaysAgo(30);
  const olderThan31Days = getUtcDaysAgo(31);
  const historyStartDate =
    latestManualUpdateDate && latestManualUpdateDate < olderThan31Days ? latestManualUpdateDate : recentHistoryStartDate;

  const sortedArray = useMemo(() => {
    if (!inventoryLogData) return [];

    const sortedLog = _.chain(inventoryLogData)
      .map((inventory) => ({ ...inventory, events: _.sortBy(inventory.events, 'time') }))
      .filter((inventory) => inventory.utcDate >= historyStartDate)
      .sortBy('utcDate')
      .value();

    return isAscending ? sortedLog : sortedLog.reverse();
  }, [inventoryLogData, historyStartDate, isAscending]);

  return (
    <>
      <Global styles={drawerOverflowStyle} />
      <Box className="feed-inventory-log" display={'flex'} flexDirection={'column'} gap={4}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} overflow={'hidden'}>
          <Typography fontWeight={'bold'} fontSize={16}>
            <FormattedMessage id={`${PAGE_NAME_ID}.title`} />
          </Typography>
          <Mui.IconButton onClick={() => setIsAscending((prev) => !prev)} sx={{ background: 'none' }}>
            <ArrowUpDown />
          </Mui.IconButton>
        </Box>
        <Divider dark />
        <Box maxHeight={'min(60vh, 400px)'} overflow={'auto'}>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Headers />
            {isFetching ? (
              <Box height={400} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <LoadingSpinner />
              </Box>
            ) : (
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                {sortedArray?.map((logItem, logIndex) => {
                  const today = getUtcToday();
                  const currentDayRow = logItem.utcDate === today;
                  const showStartingInventory = latestManualUpdateDate !== null && logItem.utcDate >= latestManualUpdateDate;

                  if (logItem.events?.length === 0) {
                    return (
                      <EmptyEventRow
                        key={logIndex}
                        date={logItem.utcDate}
                        startingInventory={logItem.startingInventory}
                        showStartingInventory={showStartingInventory}
                      />
                    );
                  }

                  return (
                    <Box key={logIndex}>
                      {logItem.events?.map((eventItem, idx) => {
                        const isFirstRow = idx === 0;
                        const isManualUpdate = eventItem.event === EventName.MANUAL_UPDATE;
                        const IconComponent = iconMap[eventItem?.event];
                        return (
                          <GridWrapper
                            key={`${logIndex}-${idx}`}
                            sx={{
                              backgroundColor: currentDayRow ? '#E6F2FA' : isManualUpdate ? 'action.selected' : '#F7F8F8',
                              borderTopLeftRadius: isFirstRow ? '8px' : '1px',
                              borderTopRightRadius: isFirstRow ? '8px' : '1px',
                              borderBottomLeftRadius: isFirstRow ? '1px' : '8px',
                              borderBottomRightRadius: isFirstRow ? '1px' : '8px',
                            }}>
                            {isFirstRow ? (
                              <Box px={1} fontSize={14}>
                                <FormattedDate value={logItem.utcDate} weekday="short" day="2-digit" month="short" />
                              </Box>
                            ) : (
                              <Box />
                            )}
                            {isFirstRow && currentDayRow && eventItem.dataInput !== 'User' ? (
                              <Box />
                            ) : (
                              <Box
                                display={'flex'}
                                alignItems={'center'}
                                justifyContent={'center'}
                                textAlign={'center'}
                                py={4}
                                fontSize={14}
                                width={'100%'}
                                minWidth={0}>
                                {eventItem.dataInput === 'User' ? (
                                  <Tooltip sx={{ whiteSpace: 'nowrap' }} title={eventItem.updatedBy} placement={'top'}>
                                    <Box display={'flex'} alignItems={'center'} gap={1}>
                                      <Typography fontSize={14} fontWeight={'bold'} sx={{ textTransform: 'capitalize' }}>
                                        {titleCase(eventItem.dataInput)}
                                      </Typography>
                                      <CircleHelp size={17} />
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  eventItem.dataInput
                                )}
                              </Box>
                            )}
                            <Box display={'flex'} alignItems={'center'} gap={2} px={2} minWidth={0}>
                              {isFirstRow && currentDayRow ? <ClipboardCheck size={20} /> : IconComponent && <IconComponent size={20} />}
                              <Typography fontSize={14} fontWeight={isManualUpdate ? 'bold' : 'regular'} minWidth={0}>
                                {isFirstRow && currentDayRow && eventItem.dataInput !== 'User' ? (
                                  <FormattedMessage id={`${PAGE_NAME_ID}.today`} />
                                ) : (
                                  eventItem.event
                                )}
                              </Typography>
                            </Box>
                            {isFirstRow && currentDayRow && eventItem.dataInput !== 'User' ? (
                              <Box />
                            ) : (
                              <Box
                                fontSize={14}
                                display={'flex'}
                                width={'100%'}
                                minWidth={0}
                                justifyContent={'flex-end'}
                                pr={2}
                                whiteSpace={'nowrap'}
                                fontWeight={isManualUpdate ? 'bold' : 'regular'}>
                                <FormattedNumber
                                  value={eventItem.adjustment}
                                  unit={'kilogram'}
                                  style="unit"
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Box>
                            )}
                            {isFirstRow && showStartingInventory && (
                              <Box fontSize={14} fontWeight={700} display={'flex'} width={'100%'} minWidth={0} justifyContent={'flex-end'} pr={2} whiteSpace={'nowrap'}>
                                <FormattedNumber
                                  value={logItem.startingInventory}
                                  unit={'kilogram'}
                                  style="unit"
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Box>
                            )}
                          </GridWrapper>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default React.memo(FeedInventoryLog);
